import React, { useState, useEffect } from 'react';
import SubsectionTitle from './SubsectionTitle';

export default function PickupLocationSelector({ settings, cartAttributes, setCartAttributes }) {
	const [locationsHtml, setLocationsHtml] = useState('');
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [updateAfterRender, setUpdateAfterRender] = useState(false);

	useEffect(() => {
		fetchLocationsSection();
	}, []);

	const updateSelectedLocation = () => {
		// First, remove all existing selections
		const allLocationCards = document.querySelectorAll('.location-card');

		allLocationCards.forEach((card) => card.classList.remove('location-card--selected'));

		// Then select the current one if it exists
		if (cartAttributes?.pickupLocation) {
			const locationCard = document.querySelector(
				`.location-card[data-location-name="${cartAttributes?.pickupLocation}"]`
			);

			if (locationCard) {
				locationCard.classList.add('location-card--selected');
			}
		}
	};

	useEffect(() => {
		updateSelectedLocation();
	}, [cartAttributes?.pickupLocation]);

	// Update selection after HTML is rendered
	useEffect(() => {
		if (locationsHtml) {
			// Small delay to ensure DOM is updated
			setTimeout(() => {
				updateSelectedLocation();
			}, 100);
		}
	}, [locationsHtml]);

	const fetchLocationsSection = async () => {
		try {
			setIsLoading(true);
			setError(null);

			// Alternative: Use section_id parameter for direct HTML response
			const response = await fetch(
				`${window.Shopify?.routes?.root || '/pages/locations'}?section_id=locations-list`,
				{
					method: 'GET',
					headers: {
						Accept: 'text/html',
						'X-Requested-With': 'XMLHttpRequest',
					},
				}
			);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const html = await response.text();

			setLocationsHtml(cleanupLocationCardHtml(html));
			updateSelectedLocation();
		} catch (error) {
			console.error('Failed to fetch locations section:', error);
			setError(settings.failedToLoadLocations);
		} finally {
			setIsLoading(false);
		}
	};

	// Handle location selection after the HTML is rendered
	useEffect(() => {
		if (locationsHtml) {
			// Add click handlers to location cards
			const locationCards = document.querySelectorAll('.location-card');

			locationCards.forEach((card) => {
				card.addEventListener('click', () => handleLocationClick(card));
			});

			// Cleanup function
			return () => {
				locationCards.forEach((card) => {
					card.removeEventListener('click', () => handleLocationClick(card));
				});
			};
		}
	}, [locationsHtml]);

	const cleanupLocationCardHtml = (html) => {
		// Create a temporary DOM element to parse and modify the HTML
		const tempDiv = document.createElement('div');
		
		tempDiv.innerHTML = html;

		// Find all location cards
		const locationCards = tempDiv.querySelectorAll('.location-card');

		locationCards.forEach((card) => {
			// Extract location name from the h3 element
			const locationNameElement = card.querySelector('h3 a, h3 span');
			const locationName = locationNameElement?.textContent?.trim();

			if (locationName) {
				// Add data attribute for selection
				card.setAttribute('data-location-name', locationName);
			}

			// Remove all links and replace with spans or divs
			const links = card.querySelectorAll('a');
			
			links.forEach((link) => {
				const span = document.createElement('span');
				
				span.innerHTML = link.innerHTML;
				span.className = link.className;
				
				link.parentNode.replaceChild(span, link);
			});

			// Remove buttons (like "Get Directions")
			const buttons = card.querySelectorAll('button');
			
			buttons.forEach((button) => button.remove());

			// Add selection styling classes
			card.classList.add('location-card--selectable');
		});

		return tempDiv.innerHTML;
	};

	const handleLocationClick = (locationCard) => {
		if (locationCard.classList.contains('location-card--selected')) {
			locationCard.classList.remove('location-card--selected');
			
			setCartAttributes((prev) => ({ ...prev, pickupLocation: null }));
		} else {
			locationCard.classList.add('location-card--selected');
			
			const locationName = locationCard.dataset.locationName;

			setCartAttributes((prev) => ({ ...prev, pickupLocation: locationName }));

			setTimeout(() => {
				const locationsContainer = document.querySelector('.locations-container');
				// if (locationsContainer) {
				//   locationsContainer.classList.add('close');
				// }

				const step4Element = document.querySelector('#step-4');
				
				if (step4Element) {
					step4Element.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
					});
				}
			}, 300);
		}
	};

	if (isLoading || error) return null;

	return (
		<div className="pickup-location-selector">
			<SubsectionTitle settings={settings} stepNumber={3} cartAttributes={cartAttributes} />

			{/* Render the locations section HTML */}
			<div className="locations-container can-be-closed" dangerouslySetInnerHTML={{ __html: locationsHtml }} />
		</div>
	);
}
